import moment from "moment";
import { useTranslation } from "react-i18next";

interface PrintableOrderProps {
	order: {
		id: number;
		createdAt: string | Date;
		totalPrice: string | number;
		deliveryFee?: string | number | null;
		paymentStatus?: string | null;
		paymentMethod?: string | null;
		estimatedDeliveryTime?: string | Date | null;
		user?: {
			name?: string | null;
			email?: string | null;
			phone?: string | null;
		} | null;
		address?: {
			streetAddress?: string | null;
			city?: string | null;
			zipCode?: string | null;
			phone?: string | null;
			instructions?: string | null;
		} | null;
		items?: {
			id: number;
			quantity: number;
			unitPrice: string | number;
			totalPrice: string | number;
			menuItem?: {
				name?: string | null;
			} | null;
			menuSize?: {
				sizeName?: string | null;
			} | null;
			extras?:
				| {
						name?: string | null;
						quantity?: number;
						totalPrice?: string | number;
				  }[]
				| null;
		}[];
	};
}

export default function PrintableOrder({ order }: PrintableOrderProps) {
	const total = Number(order.totalPrice);
	const deliveryFee = Number(order.deliveryFee ?? 0);
	const subtotal = total - deliveryFee;
	const { t } = useTranslation();

	return (
		<div className="printable-order font-mono text-sm max-w-[80mm] mx-auto p-4 hidden print:block bg-white text-black">
			<div className="text-center mb-6 border-b-2 border-dashed border-black pb-4">
				<h1 className="text-2xl font-bold uppercase tracking-tighter">
					{t("admin.printable.business_name")}
				</h1>
				<p className="text-[10px] mt-1 italic">
					{t("admin.printable.tagline")}
				</p>
				<p className="text-[10px]">{t("admin.printable.address")}</p>

				<div className="mt-6 flex justify-between items-end text-left">
					<div>
						<p className="font-bold text-lg leading-none">
							{t("admin.printable.order_number", { id: order.id })}
						</p>
						<p className="text-[10px] mt-1">
							{moment(order.createdAt).format("DD MMM YYYY · HH:mm")}
						</p>
					</div>
					<div className="text-right">
						<p className="text-[10px] uppercase font-bold">
							{order.paymentMethod || "Stripe"}
						</p>
						<p
							className={`text-[10px] font-bold ${order.paymentStatus === "paid" ? "text-green-700" : "text-red-700"}`}
						>
							{order.paymentStatus === "paid"
								? t("admin.printable.paid")
								: t("admin.printable.unpaid")}
						</p>
					</div>
				</div>
			</div>

			<div className="mb-6 space-y-1">
				<h3 className="font-bold border-b border-black mb-2 pb-1 uppercase text-xs">
					{t("admin.printable.delivery_details")}
				</h3>
				<p className="font-bold text-base leading-tight">{order.user?.name}</p>
				<p className="text-xs">{order.user?.email}</p>
				<p className="mt-2 font-bold leading-tight">
					{order.address?.streetAddress}
				</p>
				<p className="text-xs">
					{order.address?.city}, {order.address?.zipCode}
				</p>
				{(order.address?.phone || order.user?.phone) && (
					<p className="mt-1 text-xs font-bold">
						{t("admin.printable.tel")}:{" "}
						{order.address?.phone || order.user?.phone}
					</p>
				)}
				{order.estimatedDeliveryTime && (
					<p className="text-xs font-bold mt-1 text-primary">
						{t("admin.printable.est_delivery")}:{" "}
						{moment(order.estimatedDeliveryTime).format("HH:mm")}
					</p>
				)}
				{order.address?.instructions && (
					<div className="mt-2 p-2 border border-black border-dotted rounded text-[11px] leading-snug">
						<span className="font-bold uppercase text-[10px] block mb-0.5">
							{t("admin.printable.special_instructions")}
						</span>
						{order.address.instructions}
					</div>
				)}
			</div>

			<div className="mb-6">
				<h3 className="font-bold border-b border-black mb-2 pb-1 uppercase text-xs">
					{t("admin.printable.order_items")}
				</h3>
				<ul className="space-y-4">
					{order.items?.map((item, idx) => (
						<li
							key={idx}
							className="border-b border-gray-100 pb-2 last:border-0"
						>
							<div className="flex justify-between items-start font-bold">
								<span className="flex-1 pr-2">
									{item.quantity}x {item.menuItem?.name}
								</span>
								<span className="shrink-0 font-mono">
									€{(Number(item.unitPrice) * item.quantity).toFixed(2)}
								</span>
							</div>
							<p className="text-[11px] mt-0.5 text-gray-700">
								{t("admin.printable.size")}: {item.menuSize?.sizeName}
							</p>
							{item.extras && item.extras.length > 0 && (
								<ul className="mt-1.5 space-y-0.5">
									{item.extras.map((extra, eIdx) => (
										<li
											key={eIdx}
											className="text-[11px] text-gray-600 flex justify-between"
										>
											<span>
												+ {extra.name}{" "}
												{extra.quantity && extra.quantity > 1
													? `(x${extra.quantity})`
													: ""}
											</span>
											{extra.totalPrice && (
												<span className="text-[10px] italic">
													€{Number(extra.totalPrice).toFixed(2)}
												</span>
											)}
										</li>
									))}
								</ul>
							)}
						</li>
					))}
				</ul>
			</div>

			<div className="border-t-2 border-dashed border-black pt-4 mb-6">
				<div className="flex justify-between mb-1 text-xs">
					<span>{t("admin.printable.subtotal")}</span>
					<span className="font-mono">€{subtotal.toFixed(2)}</span>
				</div>
				<div className="flex justify-between mb-1 text-xs">
					<span>{t("admin.printable.delivery_fee")}</span>
					<span className="font-mono">
						{deliveryFee === 0
							? t("admin.printable.free")
							: `€${deliveryFee.toFixed(2)}`}
					</span>
				</div>
				<div className="flex justify-between font-bold text-xl mt-3 pt-3 border-t-2 border-black">
					<span>{t("admin.printable.total")}</span>
					<span className="font-mono">€{total.toFixed(2)}</span>
				</div>
				<div className="mt-6 text-center">
					<div className="inline-block border-2 border-black px-4 py-1.5 font-bold uppercase text-xs tracking-widest">
						{order.paymentStatus === "paid"
							? t("admin.printable.paid_thank_you")
							: t("admin.printable.cash_on_delivery")}
					</div>
				</div>
			</div>

			<div className="text-center text-xs">
				<p>{t("admin.printable.thank_you")}</p>
				<p>www.american-way.de</p>
			</div>

			{/* Force page break after each order when printing multiple */}
			<div className="break-after-page"></div>
		</div>
	);
}
